/**
 * BalanceSheetChart Component - Phase 5 Implementation
 * Composed chart for assets/liabilities visualization
 * Shows balance sheet structure with equity breakdown
 */

import React from 'react';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  Legend,
  ReferenceLine
} from 'recharts';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  type ChartConfig
} from '../../ui/chart';
import { Skeleton } from '../../ui/skeleton';
import { Building, CreditCard, TrendingUp, Scale } from 'lucide-react';
import { cn } from '../../../lib/utils';

interface BalanceSheetChartProps {
  data: any[];
  isLoading?: boolean;
  error?: string;
  className?: string;
}

const chartConfig = {
  assets: {
    label: "Total Assets",
    color: "hsl(var(--chart-3))",
  },
  liabilities: {
    label: "Total Liabilities",
    color: "hsl(var(--chart-4))",
  },
  equity: {
    label: "Equity",
    color: "hsl(var(--chart-1))",
  },
  ratio: {
    label: "Debt-to-Equity Ratio",
    color: "hsl(var(--chart-2))",
  },
} satisfies ChartConfig;

// Mock data for demonstration
const generateMockData = () => [
  { 
    account: 'Current Assets', 
    assets: 450000, 
    liabilities: 0, 
    equity: 450000, 
    ratio: 0,
    category: 'assets'
  },
  { 
    account: 'Fixed Assets', 
    assets: 750000, 
    liabilities: 0, 
    equity: 750000, 
    ratio: 0,
    category: 'assets'
  },
  { 
    account: 'Investments', 
    assets: 200000, 
    liabilities: 0, 
    equity: 200000, 
    ratio: 0,
    category: 'assets'
  },
  { 
    account: 'Current Liabilities', 
    assets: 0, 
    liabilities: 180000, 
    equity: -180000, 
    ratio: 0.4,
    category: 'liabilities'
  },
  { 
    account: 'Long-term Debt', 
    assets: 0, 
    liabilities: 320000, 
    equity: -320000, 
    ratio: 0.71,
    category: 'liabilities'
  },
  { 
    account: 'Shareholders Equity', 
    assets: 0, 
    liabilities: 0, 
    equity: 900000, 
    ratio: 0.56,
    category: 'equity'
  },
];

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.abs(value));
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0]?.payload;
    return (
      <div className="bg-background border rounded-lg shadow-lg p-3 min-w-[220px]">
        <p className="font-medium mb-2">{label}</p>
        {payload.map((entry: any, index: number) => {
          if (entry.value === 0) return null;
          return (
            <div key={index} className="flex items-center justify-between text-sm mb-1">
              <div className="flex items-center space-x-2">
                <div 
                  className="w-3 h-3 rounded"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-muted-foreground">{entry.name}:</span>
              </div>
              <span className="font-medium">
                {entry.dataKey === 'ratio' 
                  ? `${entry.value.toFixed(2)}`
                  : formatCurrency(entry.value)
                }
              </span>
            </div>
          );
        })}
        <div className="text-xs text-muted-foreground mt-2 pt-2 border-t">
          Category: {data?.category}
        </div>
      </div>
    );
  }
  return null;
};

export const BalanceSheetChart: React.FC<BalanceSheetChartProps> = ({
  data,
  isLoading = false,
  error,
  className
}) => {
  // Use mock data if no data provided or in loading state
  const chartData = data.length > 0 ? data : generateMockData();

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center space-y-2">
          <p className="text-destructive font-medium">Failed to load chart</p>
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <Skeleton className="h-6 w-32" />
          <div className="flex space-x-4">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-20" />
          </div>
        </div>
        <Skeleton className="h-64 w-full" />
        <div className="flex space-x-6 justify-center">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-16" />
        </div>
      </div>
    );
  }

  // Calculate summary metrics
  const totalAssets = chartData.reduce((sum, item) => sum + (item.assets || 0), 0);
  const totalLiabilities = chartData.reduce((sum, item) => sum + (item.liabilities || 0), 0);
  const totalEquity = totalAssets - totalLiabilities;
  const debtToEquityRatio = totalEquity > 0 ? totalLiabilities / totalEquity : 0;

  return (
    <div className={cn("space-y-4", className)}>
      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-green-50 dark:bg-green-950/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-700 dark:text-green-300">Total Assets</p>
              <p className="text-xl font-bold text-green-800 dark:text-green-200">
                {formatCurrency(totalAssets)}
              </p>
            </div>
            <div className="text-green-600">
              <Building className="h-6 w-6" />
            </div>
          </div>
        </div>
        
        <div className="bg-red-50 dark:bg-red-950/20 rounded-lg p-4 border border-red-200 dark:border-red-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-700 dark:text-red-300">Total Liabilities</p>
              <p className="text-xl font-bold text-red-800 dark:text-red-200">
                {formatCurrency(totalLiabilities)}
              </p>
            </div>
            <div className="text-red-600">
              <CreditCard className="h-6 w-6" />
            </div>
          </div>
        </div>
        
        <div className="bg-blue-50 dark:bg-blue-950/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-700 dark:text-blue-300">Total Equity</p>
              <p className="text-xl font-bold text-blue-800 dark:text-blue-200">
                {formatCurrency(totalEquity)}
              </p>
            </div>
            <div className="text-blue-600">
              <TrendingUp className="h-6 w-6" />
            </div>
          </div>
        </div>
        
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Debt-to-Equity</p>
              <p className={cn(
                "text-xl font-bold",
                debtToEquityRatio > 1 ? "text-orange-600" : "text-green-600"
              )}>
                {debtToEquityRatio.toFixed(2)}
              </p>
            </div>
            <div className="text-muted-foreground">
              <Scale className="h-6 w-6" />
            </div>
          </div>
        </div>
      </div>

      {/* Balance Sheet Chart */}
      <ChartContainer config={chartConfig} className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={chartData}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 60,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis 
              dataKey="account" 
              stroke="hsl(var(--muted-foreground))"
              fontSize={12}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis 
              yAxisId="currency"
              orientation="left"
              tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
              stroke="hsl(var(--muted-foreground))"
              fontSize={12}
            />
            <YAxis 
              yAxisId="ratio"
              orientation="right"
              tickFormatter={(value) => value.toFixed(1)}
              stroke="hsl(var(--muted-foreground))"
              fontSize={12}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend content={<ChartLegendContent />} />
            
            <ReferenceLine yAxisId="currency" y={0} stroke="hsl(var(--border))" strokeDasharray="2 2" />
            
            <Bar
              yAxisId="currency"
              dataKey="assets"
              fill="var(--color-assets)"
              name="Assets"
              radius={[2, 2, 0, 0]}
            />
            <Bar
              yAxisId="currency"
              dataKey="liabilities"
              fill="var(--color-liabilities)"
              name="Liabilities"
              radius={[2, 2, 0, 0]}
            />
            <Bar
              yAxisId="currency"
              dataKey="equity"
              fill="var(--color-equity)"
              name="Equity"
              radius={[2, 2, 0, 0]}
            />
            <Line
              yAxisId="ratio"
              type="monotone"
              dataKey="ratio"
              stroke="var(--color-ratio)"
              strokeWidth={3}
              dot={{ fill: "var(--color-ratio)", strokeWidth: 2, r: 4 }}
              name="D/E Ratio"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartContainer>
    </div>
  );
};